/** Small pieces shared by the herd screens: option labels, subject helpers, record shortcuts. */
import { useId } from "react";
import { localDate, type AnimalDetail, type EventInput, type ObservedTime, type SubjectKind, type SubjectRef } from "../../../contract";
import { openRecord } from "../../../web/forms/EventForm";
import { t } from "../../../web/lib/i18n";
import { useMe } from "../../../web/lib/session";
import type { HerdFacet } from "../shared/lifecycle";

export const opt = (map: Record<string, string>, key: string | null | undefined): string => (key ? t(map[key] ?? key) : "");

export function useToday(): string {
  const { data: me } = useMe();
  return me?.ranch ? localDate(new Date(), me.ranch.timezone) : new Date().toISOString().slice(0, 10);
}

export const ids = (subjects: SubjectRef[], role: string) => subjects.filter((s) => s.role === role).map((s) => s.id);

export function withRole(subjects: SubjectRef[], role: string, kind: SubjectKind, next: string | string[] | null): SubjectRef[] {
  const list = (Array.isArray(next) ? next : next ? [next] : []).map((id) => ({ kind, id, role }));
  return [...subjects.filter((s) => s.role !== role), ...list];
}

export const labelsOf = (subjects: SubjectRef[] | undefined): Record<string, string> =>
  Object.fromEntries((subjects ?? []).filter((s) => s.label).map((s) => [s.id, s.label!]));

export const herdOf = (a: { facets: Record<string, unknown> }): HerdFacet | undefined => a.facets.herd as HerdFacet | undefined;

export const isCurrent = (a: { status: string }) => a.status === "active" || a.status === "missing";

/** Open the record sheet for a herd event with these subjects and data. */
export function recordHerd(type: string, subjects: SubjectRef[], data: Record<string, unknown> = {}, extra: Partial<EventInput> = {}): void {
  openRecord({ initial: { type, subjects, data, ...extra } });
}

export const animalRef = (a: Pick<AnimalDetail, "id" | "label">, role = "animal"): SubjectRef => ({ kind: "animal", id: a.id, role, label: a.label });

/** Stable ids for tying field labels to their inputs: `const id = useIds(); <Field htmlFor={id("name")}>`. */
export function useIds(): (key: string) => string {
  const base = useId();
  return (key) => `${base}${key}`;
}

export const dayOf =(o: ObservedTime | undefined): string | undefined => (o?.value ? o.value.slice(0, 10) : undefined);
